import React, { useEffect, useMemo, useRef, useState } from 'react';
import { UUID, LoggerService } from '@umanforge/common';
import { ChevronsLeftIcon } from '@common_components/icons/chevronsLeft.icon';
import { ChevronsRightIcon } from '@common_components/icons/chevronsRight.icon';
import type { ChartSectionApi } from 'interfaces/chartSection.interface';
const logger = new LoggerService( 'ChartSectionsHeader' );

type ChartSectionsHeaderProps = {
  chartSections: ChartSectionApi[];
  selectedSectionId: UUID | null;
  onSelectSection: ( sectionId: UUID ) => void;
};

const ChartSectionsHeader: React.FC<ChartSectionsHeaderProps> = ( {
  chartSections,
  selectedSectionId,
  onSelectSection,
} ) => {
  const maxVisibleCount = 5;
  const minVisibleCount = 1;
  const minButtonWidth = 140;
  const gapWidth = 8;

  const listContainerRef = useRef<HTMLDivElement | null>( null );
  const [ visibleCount, setVisibleCount ] = useState( maxVisibleCount );
  const [ startIndex, setStartIndex ] = useState( 0 );

  useEffect( () => {
    const computeVisibleCount = () => {
      try {
        const containerWidth = listContainerRef.current?.offsetWidth ?? 0;
        if ( containerWidth <= 0 ) {
          setVisibleCount( maxVisibleCount );
          return;
        }
        const computed = Math.floor( ( containerWidth + gapWidth ) / ( minButtonWidth + gapWidth ) );
        const nextVisibleCount = Math.max( minVisibleCount, Math.min( maxVisibleCount, computed ) );
        setVisibleCount( nextVisibleCount );
      } catch ( e ) {
        logger.warn( 'Error computing visible count', e );
      }
    };

    computeVisibleCount();

    const currentContainer = listContainerRef.current;
    if ( !currentContainer || typeof ResizeObserver === 'undefined' ) {
      window.addEventListener( 'resize', computeVisibleCount );
      return () => {
        window.removeEventListener( 'resize', computeVisibleCount );
      };
    }

    let rafId: number | null = null;
    const observer = new ResizeObserver( () => {
      if ( rafId !== null ) { cancelAnimationFrame( rafId ); }
      rafId = requestAnimationFrame( () => {
        try {
          computeVisibleCount();
        } catch ( e ) {
          logger.warn( 'Error in ResizeObserver', e );
        }
      } );
    } );
    observer.observe( currentContainer );

    return () => {
      if ( rafId !== null ) { cancelAnimationFrame( rafId ); }
      observer.disconnect();
    };
  }, [] );

  useEffect( () => {
    try {
      if ( !selectedSectionId || chartSections.length === 0 ) {
        return;
      }
      const selectedIdx = chartSections.findIndex( s => {return s.id === selectedSectionId;} );
      if ( selectedIdx === -1 ) {
        return;
      }
      const maxStart = Math.max( chartSections.length - visibleCount, 0 );
      let newStart = startIndex;
      if ( selectedIdx < startIndex ) {
        newStart = selectedIdx;
      } else if ( selectedIdx >= startIndex + visibleCount ) {
        newStart = selectedIdx - visibleCount + 1;
      }
      newStart = Math.max( 0, Math.min( newStart, maxStart ) );
      if ( newStart !== startIndex ) {
        setStartIndex( newStart );
      }
    } catch ( e ) {
      logger.warn( 'Error keeping selected section visible', e );
    }
  }, [ chartSections, selectedSectionId, visibleCount ] );

  const visibleSections = useMemo( () => {
    return chartSections.slice( startIndex, startIndex + visibleCount );
  }, [ chartSections, startIndex, visibleCount ] );

  const shouldCenterVisibleSections = visibleSections.length < visibleCount;

  const canGoLeft = startIndex > 0;
  const canGoRight = startIndex + visibleCount < chartSections.length;

  const handleGoLeft = () => {
    if ( !canGoLeft ) {
      return;
    }
    setStartIndex( prev => { return Math.max( prev - 1, 0 ); } );
  };

  const handleGoRight = () => {
    if ( !canGoRight ) {
      return;
    }
    setStartIndex( prev => { return Math.min( prev + 1, chartSections.length - visibleCount ); } );
  };

  return (
    <div className="rounded-xl border border-gray-100 bg-white px-2 py-2 sm:px-3">
      <div className="flex items-center gap-2">
        {canGoLeft && (
          <button
            type="button"
            onClick={handleGoLeft}
            className="flex shrink-0 items-center justify-center h-8 w-8 rounded-lg border border-gray-200 bg-gray-50 text-gray-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100 transition-colors"
            aria-label="Sections précédentes"
          >
            <ChevronsLeftIcon className="w-4 h-4" />
          </button>
        )}
        <div
          ref={listContainerRef}
          className="flex-1 overflow-hidden"
        >
          <div
            className={shouldCenterVisibleSections ? 'flex flex-nowrap items-center justify-center gap-2 pr-1' : 'grid gap-2 pr-1'}
            style={shouldCenterVisibleSections ? undefined : { gridTemplateColumns: `repeat(${visibleCount}, minmax(0, 1fr))` }}
          >
            {visibleSections.map( section => {
              const isActive = section.id === selectedSectionId;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => { return onSelectSection( section.id ); }}
                  className={`relative min-w-0 px-3 py-2 rounded-lg border text-sm font-medium whitespace-nowrap truncate focus:outline-none focus:ring-2 focus:ring-blue-100 ${
                    isActive
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-blue-200 hover:bg-blue-50'
                  }`}
                  style={{
                    boxShadow: isActive ? '0 2px 8px 0 rgba(59,130,246,0.04)' : undefined,
                    zIndex: isActive ? 1 : undefined,
                  }}
                  title={section.name}
                >
                  <span
                    className={`absolute left-3 right-3 bottom-1 h-0.5 rounded-full bg-blue-500 transition-all duration-300 ${isActive ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'}`}
                    aria-hidden="true"
                  />
                  <span className="relative z-10">{section.name}</span>
                </button>
              );
            } )}
          </div>
        </div>
        {canGoRight && (
          <button
            type="button"
            onClick={handleGoRight}
            className="flex shrink-0 items-center justify-center h-8 w-8 rounded-lg border border-gray-200 bg-gray-50 text-gray-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100 transition-colors"
            aria-label="Sections suivantes"
          >
            <ChevronsRightIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export { ChartSectionsHeader };
